import { useQueryClient } from "@tanstack/react-query";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import type { RenderEditOptions } from "components/MainSectionTemplate/types";
import TimetableDayOffsetDropdown from "components/dropdowns/reactselect/TimetableDayOffsetDropdown";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import type moment from "moment";
import React from "react";
import {
  formatDayOffset,
  formatTimetableEntryType,
  getTimetableEntryFieldMetaData,
  getUpdateTimetableEntryInstruction,
  maxTimetableNotesLength,
  mapTimetableEntryToFormState,
  type TimetableEntryFormState
} from "shared/utils/fieldsHelper/timetableEntryFields";
import { invalidateTimetableCache } from "utilities/api/timetableQuery";
import { TimetableTimeInput } from "timetable/TimetableTimeInput";
import { TimetableTypeInput, type TimetableTypeInputValue } from "timetable/TimetableTypeInput";
import { formatTime, newLineToBr } from "utilities";

interface Props {
  entry: server.dto.TimetableEntry
  canModify: boolean
}

export const TimetableSidePanelDetails = (props: Props) => {
  const queryClient = useQueryClient();
  const [formState, setFormState] = React.useState<TimetableEntryFormState>(mapTimetableEntryToFormState(props.entry));

  const onValueChanged = (key: string, value: any) => {
    if (key === "startTime") {
      setFormState(prevState => ({ ...prevState, startTime: value }));
    } else if (key === "endTime") {
      setFormState(prevState => ({ ...prevState, endTime: value }));
    } else if (key === "date") {
      setFormState(prevState => ({ ...prevState, dayOffset: value }));
    } else if (key === "type") {
      setFormState(prevState => ({ ...prevState, type: value.type, customText: value.customText }));
    } else if (key === "notes") {
      setFormState(prevState => ({ ...prevState, notes: value }));
    }
  };

  const update = (newState: Partial<TimetableEntryFormState>) => window.CoreApi.Timetable.UpdateEntry(
    getUpdateTimetableEntryInstruction(props.entry.timetableEntryId, { ...formState, ...newState })
  );

  const fieldsMetaData = getTimetableEntryFieldMetaData(formState, props.canModify);
  const endTimeMeta = fieldsMetaData.endTime.fields();

  return <SidePanelDetailSectionTemplate
    isInPageEditable={props.canModify}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={async () => { invalidateTimetableCache(queryClient); }}
    sections={[{
      key: "timetableEntry",
      type: "regular",
      hideTitle: true,
      title: RESX.Timetable.TimetableItem,
      fields: [{
        key: "startTime",
        label: RESX.DateTime.Starts,
        render: () => <>{formatTime(formState.startTime)}</>,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<moment.Moment>) => <TimetableTimeInput
            value={value}
            autoFocus
            validatorKey="startTime"
            isRequired={formState.endTime != null}
            onChange={(startTime, submit) => onChange(startTime, submit)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.startTime,
          submitChange: (startTime: moment.Moment) => update({ startTime })
        },
        ...fieldsMetaData.startTime.fields()
      }, {
        key: "endTime",
        label: RESX.DateTime.Ends,
        render: () => <>{formatTime(formState.endTime)}</>,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<moment.Moment>) => <TimetableTimeInput
            value={value}
            autoFocus
            validatorKey="endTime"
            isRequired={false}
            onChange={(endTime, submit) => onChange(endTime, submit)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.endTime,
          submitChange: (endTime: moment.Moment) => update({ endTime })
        },
        ...endTimeMeta,
        editDisabled: endTimeMeta.editDisabled || formState.startTime == null
      }, {
        key: "date",
        label: RESX.DateTime.lblDate,
        render: () => <>{formatDayOffset(formState.dayOffset, RESX.Show.DateOfEvent)}</>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <TimetableDayOffsetDropdown
            value={value}
            onChange={dayOffset => onChange(dayOffset, true)}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: formState.dayOffset,
          submitChange: (dayOffset: number) => update({ dayOffset })
        },
        ...fieldsMetaData.dayOffset.fields()
      }, {
        key: "type",
        label: RESX.GeneralLabels.Type,
        render: () => <>{formatTimetableEntryType(formState.type, formState.customText)}</>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<TimetableTypeInputValue>) => <TimetableTypeInput
            value={value}
            focusOnMount
            validatorKey="type"
            onChange={(v, save) => onChange(v, save)}
            submit={submit}
            cancel={cancel}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: { type: formState.type, customText: formState.customText },
          submitChange: (value: TimetableTypeInputValue) => update({ type: value.type, customText: value.customText })
        },
        ...fieldsMetaData.type.fields()
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <div style={{ wordBreak: "break-word" }}>{newLineToBr(formState.notes)}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            value={value}
            focusOnMount
            rows={3}
            maxLength={maxTimetableNotesLength}
            onChange={notes => onChange(notes)}
            onCtrlEnter={submit}
            onEsc={cancel}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: formState.notes,
          submitChange: (notes: string) => update({ notes })
        },
        ...fieldsMetaData.notes.fields()
      }]
    }]}
  />;
};
